'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { PortableTextBlock } from '@sanity/types';
import { PortableText } from '@portabletext/react';
import { ArbitraryTypedObject } from '@portabletext/types';
import { SlArrowRightCircle } from 'react-icons/sl';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import { BlurInH2 } from '@/components/eldora-ui/blur-in-h2';
import KeyFeatures, { KeyFeature } from '@/components/KeyFeatures';
import { getSanityImageUrl } from '@/libs/sanity/client';
import VideoModal from '@/components/VideoModal';
import { Video } from '@/components/LeftTextRightImageAndVideo';
import MediaModal from '@/components/ui/media-modal';
import { useMediaQuery } from 'usehooks-ts';
import { TABLET_SCREEN_SIZE } from '@/utils/constants';

type PortableTextContent =
  | PortableTextBlock
  | ArbitraryTypedObject
  | (PortableTextBlock | ArbitraryTypedObject)[];

type ReadMoreLink = {
  label?: string;
  external?: string;
  internal?: {
    ref?: {
      metadata?: {
        slug?: {
          current?: string;
        };
      };
    };
  };
};
type PopupImage = {
  image: any;
  description: PortableTextContent;
  enlargeButtonLabel: string;
};

type LeftTextRightImageProps = {
  anchor?: string;
  title?: string;
  popupImage?: PopupImage;
  image: any; // Consider using a more specific type for the image
  leftContent: PortableTextContent;
  readMoreLink?: ReadMoreLink;
  imageDescription?: string;
  keyFeaturesTitle?: string;
  keyFeatures?: KeyFeature[];
  bottomContent?: PortableTextContent;
  bottomImage?: any;
  bottomPopupVideo?: Video;
};

const LeftTextRightImage: React.FC<LeftTextRightImageProps> = ({
  anchor,
  title,
  popupImage,
  image,
  leftContent,
  readMoreLink,
  imageDescription,
  keyFeaturesTitle,
  keyFeatures,
  bottomContent,
  bottomImage,
  bottomPopupVideo,
}) => {
  const isExternal = Boolean(readMoreLink?.external);
  const linkUrl = isExternal
    ? readMoreLink?.external
    : (readMoreLink?.internal?.ref?.metadata?.slug?.current ?? '#');
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);

  return (
    <section id={anchor} className="max-w-8xl mx-auto mt-12 scroll-mt-[150px]">
      <div className="grid grid-cols-12 gap-x-8">
        <LeftContent
          title={title}
          content={leftContent}
          bottomImage={bottomImage}
          bottomPopupVideo={bottomPopupVideo}
          isFullWidth={image === undefined}
        />
        <RightContent
          image={image}
          popupImage={popupImage}
          title={title}
          readMoreLink={readMoreLink}
          imageDescription={imageDescription}
          linkUrl={linkUrl}
          isTablet={isTablet}
        />
      </div>
      {keyFeatures && (
        <KeyFeatures title={keyFeaturesTitle} keyFeatures={keyFeatures} />
      )}
      {bottomContent && (
        <div className="mt-4">
          <PortableTextWrapper value={bottomContent} />
        </div>
      )}
    </section>
  );
};

const LeftContent: React.FC<{
  title?: string;
  content: PortableTextContent;
  bottomImage?: any;
  bottomPopupVideo?: Video;
  isFullWidth?: boolean;
}> = ({ title, content, bottomImage, bottomPopupVideo, isFullWidth }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div
      className={`col-span-12 xl:col-span-7 ${isFullWidth ? 'xl:col-span-12' : ''}`}
    >
      <BlurInH2>{title}</BlurInH2>
      {content && <PortableTextWrapper value={content} />}
      {bottomImage && (
        <div className="mt-8">
          <Image
            src={getSanityImageUrl(bottomImage)}
            alt={title ?? 'Feature image'}
            className="h-full object-cover w-auto mb-2"
            width={614}
            height={349}
          />
        </div>
      )}
      {bottomPopupVideo && bottomPopupVideo.videoUrl && (
        <div className="mt-3">
          <div
            className="flex-col cursor-pointer"
            onClick={() => setIsModalOpen(true)}
          >
            {bottomPopupVideo.buttonLabel && (
              <span className="font-medium text-primary">
                {bottomPopupVideo.buttonLabel}
              </span>
            )}
            {bottomPopupVideo.buttonLongLabel && (
              <>
                <div className="text-primary">
                  <PortableText value={bottomPopupVideo.buttonLongLabel} />
                </div>
                <SlArrowRightCircle
                  className="text-primary mt-1 block"
                  size={18}
                />
              </>
            )}
          </div>
          <VideoModal
            video={bottomPopupVideo}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
          />
        </div>
      )}
    </div>
  );
};

const RightContent: React.FC<{
  image: any;
  title?: string;
  readMoreLink?: ReadMoreLink;
  imageDescription?: string;
  popupImage?: PopupImage;
  linkUrl: string;
  isTablet: boolean;
}> = ({
  image,
  title,
  readMoreLink,
  linkUrl,
  imageDescription,
  popupImage,
  isTablet,
}) => (
  <>
    {image && (
      <div className="col-span-12 xl:col-span-5 pt-5 flex flex-col">
        <Image
          src={getSanityImageUrl(image)}
          alt={title ?? 'Feature image'}
          className="h-full object-cover w-auto mb-2"
          width={825}
          height={580}
        />
        {readMoreLink && readMoreLink.label && (
          <Link href={linkUrl} className="text-primary mt-1 hover:underline">
            {readMoreLink.label}
            <SlArrowRightCircle className="text-primaryio" size={18} />
          </Link>
        )}
        {imageDescription && (
          <p className="mt-2 font-semibold">{imageDescription}</p>
        )}
      </div>
    )}
    {popupImage && (
      <div className="col-span-12 xl:col-span-5 pt-5 flex flex-col">
        <MediaModal
          className="h-[155px] object-cover"
          imgSrc={getSanityImageUrl(popupImage.image)}
          buttonLabel={popupImage.enlargeButtonLabel}
          isObjectFitCover={isTablet}
        ></MediaModal>
      </div>
    )}
  </>
);

export default LeftTextRightImage;
